"use client";
import React, { useCallback, useEffect, useState } from "react";
import type {
  TextFieldProps as AriaTextFieldProps,
  TextFieldRenderProps,
  ValidationResult,
} from "react-aria-components";
import { TextField as AriaTextFieldComponent } from "react-aria-components";
import { HexColorPicker } from "react-colorful";
import { twMerge } from "tailwind-merge";
import { composeTailwindRenderProps } from "../../utils";
import { controlStyles } from "../../utils/controlStyles";
import { Button } from "../Button";
import {
  type BaseInputProps,
  Description,
  FieldError,
  getInputBaseStyles,
  Input,
  InputWrapper,
  Label,
  useInputFocus,
} from "../Field";
import { Popover } from "../Popover";

/**
 * Validates if a string is a valid hex color code
 * Accepts formats: #RGB, #RRGGBB, RGB, RRGGBB
 */
function isValidHexColor(value: string): boolean {
  if (!value) return false;
  const hex = value.startsWith("#") ? value.slice(1) : value;
  return /^[0-9A-Fa-f]{3}$|^[0-9A-Fa-f]{6}$/.test(hex);
}

/**
 * Normalizes a hex color to always include # and be uppercase
 * Expands 3-digit hex to 6-digit format
 */
function normalizeHexColor(value: string): string {
  if (!value) return "";
  let hex = value.startsWith("#") ? value.slice(1) : value;

  // Expand 3-digit hex to 6-digit
  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((char) => char + char)
      .join("");
  }

  return `#${hex.toUpperCase()}`;
}

/**
 * ColorField Props
 */
export interface ColorFieldProps extends Omit<AriaTextFieldProps, "isRequired" | "size" | "className">, BaseInputProps {
  /** Label text displayed above the color field */
  label?: string;
  /** Helper text displayed below the color field */
  description?: string;
  /** Error message or function to generate error message */
  errorMessage?: string | ((validation: ValidationResult) => string);
  /** Placeholder text for the input */
  placeholder?: string;
  /** Optional tooltip text to show next to the label */
  tooltip?: string;
  /** Whether the field is required */
  isRequired?: boolean;
  /** Additional CSS classes to apply */
  className?: string;
  /** Validation result object */
  validationResult?: ValidationResult;
  /** The current hex color value (controlled) */
  value?: string;
  /** Default hex color value (uncontrolled) */
  defaultValue?: string;
  /** Callback when the color changes */
  onChange?: (color: string) => void;
  /** Whether to show the color swatch preview */
  showColorSwatch?: boolean;
}

/**
 * ColorField Component
 *
 * A form control that allows users to input hex color codes manually
 * or select colors using a visual color picker. Integrates seamlessly
 * with the Edges design system.
 *
 * @example
 * ```tsx
 * <ColorField
 *   label="Brand Color"
 *   value={color}
 *   onChange={setColor}
 *   description="Choose your primary brand color"
 * />
 * ```
 */
export function ColorField({
  label,
  description,
  errorMessage,
  size = "md",
  tooltip,
  isRequired,
  transparent,
  className,
  validationResult,
  value: controlledValue,
  defaultValue,
  onChange,
  placeholder = "#000000",
  showColorSwatch = true,
  reserveErrorSpace = true,
  ...props
}: ColorFieldProps) {
  const [internalValue, setInternalValue] = useState(defaultValue || "");
  const [inputValue, setInputValue] = useState(controlledValue || defaultValue || "");
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);
  const { isFocused, handleFocus, handleBlur } = useInputFocus();

  // Use controlled value if provided, otherwise use internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;

  // Sync inputValue with controlled value when it changes
  useEffect(() => {
    if (controlledValue !== undefined) {
      setInputValue(controlledValue);
    }
  }, [controlledValue]);

  // Validate the current value
  const isInvalid = value ? !isValidHexColor(value) : false;

  // Handle input change
  const handleInputChange = useCallback(
    (newValue: string) => {
      setInputValue(newValue);

      // Only update the actual color value if it's valid
      if (isValidHexColor(newValue)) {
        const normalized = normalizeHexColor(newValue);
        if (onChange) {
          onChange(normalized);
        } else {
          setInternalValue(normalized);
        }
      } else if (newValue === "") {
        // Allow clearing the value
        if (onChange) {
          onChange("");
        } else {
          setInternalValue("");
        }
      }
    },
    [onChange]
  );

  // Handle color picker change
  const handlePickerChange = useCallback(
    (newColor: string) => {
      const normalized = normalizeHexColor(newColor);
      setInputValue(normalized);
      if (onChange) {
        onChange(normalized);
      } else {
        setInternalValue(normalized);
      }
    },
    [onChange]
  );

  // Handle input blur - normalize valid values
  const handleInputBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (isValidHexColor(inputValue)) {
        const normalized = normalizeHexColor(inputValue);
        setInputValue(normalized);
        if (onChange && normalized !== value) {
          onChange(normalized);
        } else if (!onChange && normalized !== internalValue) {
          setInternalValue(normalized);
        }
      }
      handleBlur(e, props.onBlur);
    },
    [inputValue, value, internalValue, onChange, handleBlur, props.onBlur]
  );

  return (
    <AriaTextFieldComponent
      {...props}
      isRequired={isRequired}
      className={composeTailwindRenderProps(className, "group flex flex-col")}
    >
      {({ isDisabled }: TextFieldRenderProps) => (
        <div className="flex flex-col">
          {label && (
            <Label size={size} tooltip={tooltip} isRequired={isRequired}>
              {label}
            </Label>
          )}
          <InputWrapper>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Input
                  size={size}
                  disabled={isDisabled}
                  type="text"
                  value={inputValue}
                  placeholder={placeholder}
                  onFocus={(e) => handleFocus(e, props.onFocus)}
                  onBlur={handleInputBlur}
                  onChange={(e) => handleInputChange(e.target.value)}
                  className={twMerge(
                    getInputBaseStyles({
                      isInvalid,
                      isDisabled,
                      isFocused,
                      size,
                      transparent,
                    }),
                    showColorSwatch ? "pr-12" : "pr-3",
                    "pl-3"
                  )}
                />
                {showColorSwatch && (
                  <div
                    className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    style={{ width: controlStyles[size].height }}
                  >
                    <div
                      className={twMerge(
                        "w-6 h-6 rounded border shadow-sm",
                        isValidHexColor(value) ? "border-border-default" : "border-border-default bg-background-muted"
                      )}
                      style={isValidHexColor(value) ? { backgroundColor: value } : undefined}
                      aria-hidden="true"
                    >
                      {!isValidHexColor(value) && (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="w-3 h-3 rounded-full border-2 border-border-muted" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div ref={setTriggerElement}>
                <Button
                  variant="secondary"
                  size={size}
                  icon="Palette"
                  onPress={() => setIsPickerOpen(!isPickerOpen)}
                  isDisabled={isDisabled}
                  className="flex-shrink-0"
                >
                  Pick
                </Button>
              </div>
            </div>
          </InputWrapper>
          {reserveErrorSpace ? (
            <div className="mt-1 min-h-[20px]">
              {typeof errorMessage === "string" ? (
                <FieldError size={size}>{errorMessage}</FieldError>
              ) : typeof errorMessage === "function" && validationResult ? (
                <FieldError size={size}>{errorMessage(validationResult)}</FieldError>
              ) : description ? (
                <Description size={size}>{description}</Description>
              ) : null}
            </div>
          ) : (
            <>
              {typeof errorMessage === "string" ? (
                <FieldError size={size} className="mt-1">
                  {errorMessage}
                </FieldError>
              ) : typeof errorMessage === "function" && validationResult ? (
                <FieldError size={size} className="mt-1">
                  {errorMessage(validationResult)}
                </FieldError>
              ) : description ? (
                <Description size={size} className="mt-1">
                  {description}
                </Description>
              ) : null}
            </>
          )}

          {/* Color Picker Popover */}
          {isPickerOpen && triggerElement && (
            <Popover
              triggerRef={{ current: triggerElement }}
              isOpen={isPickerOpen}
              onOpenChange={setIsPickerOpen}
              placement="bottom end"
              className="bg-background-input shadow-xl border border-border-input/20 backdrop-blur-sm rounded-lg p-4"
            >
              <div className="flex flex-col gap-4">
                <HexColorPicker
                  color={value || "#000000"}
                  onChange={handlePickerChange}
                  style={{ width: "200px", height: "200px" }}
                />
                <div className="flex items-center gap-2">
                  <div
                    className="w-10 h-10 rounded border border-border-default shadow-sm flex-shrink-0"
                    style={{ backgroundColor: value || "#000000" }}
                  />
                  <div className="flex-1 text-text-body text-[length:var(--control-text-sm)] font-mono">
                    {value || "#000000"}
                  </div>
                </div>
              </div>
            </Popover>
          )}
        </div>
      )}
    </AriaTextFieldComponent>
  );
}
